<template>
    <modal @close="closeModal" dialog="modal-lg" class="dark-top">
        <div slot="title">
            <h2>Pricing Rules – Maximize Your Revenue with Smart Pricing</h2>
        </div>
        <div class="d-flex flex-column p-4 ml-1 fs-normal modal-content-fixed" slot="body">
            <div v-if="currentPage === 1">
                <h3>1. What is Pricing Rules?</h3>
                <ul class="ps-4 p-3">
                    <li class="mb-2">
                        <span>With Pricing Rules, you can optimize your pricing strategy automatically. This feature
                            allows you to:</span>
                    </li>
                    <li class="custom-bullet mb-2 ml-4">&#8226;&nbsp;Adjust prices based on real-time demand</li>
                    <li class="custom-bullet mb-2 ml-4">&#8226;&nbsp;Set different pricing by sales channel and day
                        of
                        the
                        week
                    </li>
                    <li class="custom-bullet mb-4 ml-4">&#8226;&nbsp;Automatically close sales channels as
                        occupancy
                        increases</li>

                    <li>
                        <span>No more static prices or manual adjustments — let your pricing work for you.</span>
                    </li>
                </ul>
            </div>
            <div v-if="currentPage === 2">
                <h3>2. Why Use It?</h3>
                <ul class="ps-4 p-3">
                    <li class="mb-2">
                        <span>When your activities are in high demand, you might be undercharging — or filling seats
                            through low-margin channels. Pricing Rules solves that by:</span>
                    </li>
                    <li class="custom-bullet mb-2 ml-4">&#8226;&nbsp;Increasing prices when demand is high</li>
                    <li class="custom-bullet mb-2 ml-4">&#8226;&nbsp;Prioritizing more profitable channels</li>
                    <li class="custom-bullet  mb-2 ml-4">&#8226;&nbsp;Avoiding last-minute underselling</li>
                    <li>
                        <span><b>“If you don’t sell those last seats today, you can’t sell them
                                tomorrow.”</b></span>
                    </li>
                    <li class="mt-2">With Pricing Rules, you stay in control and make the most of every seat.</li>
                </ul>
            </div>
            <div v-if="currentPage === 3">
                <h3>3. Real-Life Impact</h3>
                <ul class="ps-4 p-3">
                    <li><span><i class="fas fa-chart-bar"></i>Example: You sell 20 seats per day at
                            €100.</span></li>
                    <li><span>→ Revenue = €2,000/day</span></li>
                    <li class="mt-1"><span>Now apply Pricing Rules:</span></li>
                    <li class="ml-4"><span>- After 60% occupancy: price increases 5% (€105)</span></li>
                    <li class="mt-1 ml-4"><span>- After 80% occupancy: price increases 10% (€110)</span></li>
                    <li class="mt-1"><span><i class="fas fa-bullseye fa-spin" style="color: #e66565;"></i>New revenue:
                            €2,060/day</span></li>
                    <li><span><i class="fas fa-chart-line fa-sm" style="color: #62b3fe;"></i>Annual impact: +€21,900 (1
                            activity/day x 365)</span></li>
                    <li class="mt-2"><span>This is just one of many pricing scenarios you can automate.</span></li>
                </ul>
            </div>
            <div v-if="currentPage === 4">
                <h3>4. How You Can Use It?</h3>
                <ul class="ps-4 p-3">
                    <li class="mb-4">
                        <b><i class="fas fa-check-square" style="color: #32e275;"></i> Dynamic Pricing by Demand</b><br>
                        <span>Raise prices when you hit a certain % of capacity</span>
                    </li>
                    <li class="mb-4">
                        <b><i class="fas fa-check-square" style="color: #32e275;"></i> Channel Management</b><br>
                        <span>Close sales channels once your occupancy reaches a threshold — and keep the last seats for
                            the best margins</span>
                    </li>
                    <li class="mb-4">
                        <b><i class="fas fa-check-square" style="color: #32e275;"></i> Weekday/Weekend Pricing</b><br>
                        <span>Automatically apply different prices by day of the week</span>
                    </li>
                </ul>
            </div>
            <div v-if="currentPage === 5">
                <h3>5. Rule Variables You Can Configure</h3>
                <div class="ps-4">
                    <table class="table">
                        <thead>
                            <tr>
                                <th class="col-variable">Variable</th>
                                <th class="col-action">Action</th>
                                <th class="col-notes">Notes</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(row, j) in tableData.rows" :key="`row-${j}`">
                                <td>{{ row.variable }}</td>
                                <td>{{ row.action }}</td>
                                <td>{{ row.notes }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            <div v-if="currentPage === 6">
                <h3>6. Ready to Get Started?</h3>
                <ul class="ps-4 p-3">
                    <li class="mb-4"><span>Take full control of your pricing — no more guesswork, no more missed
                            revenue</span></li>
                    <li class="mb-4"><span><i class="fas fa-rocket" style="color: #bb2525;"></i>Start boosting your
                            revenue
                            automatically</span></li>
                    <li class="mb-4"><span><i class="fas fa-wrench fa-rotate-270" style="color: #657186;"></i>Easy to
                            configure</span></li>
                    <li class="mb-4"><span><i class="fas fa-chart-line fa-sm" style="color: #62b3fe;"></i>Proven
                            results</span></li>
                </ul>
            </div>
        </div>

        <div slot="footer" class="d-flex justify-content-center">
            <button v-if="currentPage > 1" class="btn blue wider footer-btn" @click="prevPage">
                Back
                <i class="fas fa-chevron-left next-icon-space mt-0"></i>
            </button>
            <button v-if="currentPage < 6" class="btn blue wider footer-btn" @click="nextPage">
                Next
                <i class="fas fa-chevron-right next-icon-space mt-0"></i>
            </button>
            <button v-if="currentPage === 6" class="btn green wider footer-btn" @click="activate">
                Activate
            </button>
        </div>
    </modal>
</template>

<script>
    import { mapActions, mapGetters } from 'vuex'
    import Modal from './Modal'

    export default {
        data() {
            return {
                currentPage: 1,
                tableData: {
                    titles: [
                        {
                            title: 'Variable',
                            width: '25%'
                        },
                        {
                            title: 'Action',
                            width: '50%'
                        },
                        {
                            title: 'Notes',
                            width: '25%'
                        }
                    ],
                    rows: [
                        {
                            variable: 'Sales Channel + Occupancy',
                            action: 'Open/Close Channel',
                            notes: 'Close channels based on occupancy'
                        },
                        {
                            variable: 'Sales Channel + Occupancy',
                            action: 'Price Increase',
                            notes: 'Adjust prices by channel based on occupancy'
                        },
                        {
                            variable: 'Sales Channel + Number of Seats Sold',
                            action: 'Close Channel',
                            notes: 'Channel closes after X seats sold '
                        },
                        {
                            variable: 'Base Price + Occupancy',
                            action: 'Price Increase',
                            notes: 'Base price adjusts with occupancy'
                        },
                        {
                            variable: 'Day of the Week + Occupancy',
                            action: 'Price Increase',
                            notes: 'Adjusts prices based on day of the week and occupancy'
                        }
                    ]
                }
            };
        },
        computed: {
            ...mapGetters([
                'getSubscriptionCards'
            ]),
            subscriptionCards() {
                return this.getSubscriptionCards;
            }
        },
        methods: {
            ...mapActions([
                'closeModal',
                'setCardSubscription',
                'openModal'
            ]),
            nextPage() {
                if (this.currentPage < 6) this.currentPage++;
            },
            prevPage() {
                if (this.currentPage > 1) this.currentPage--;
            },
            activate() {
                this.setCardSubscription(this.subscriptionCards.find(e => e.codeName === 'DynamicPricingRevenue'));
                this.openModal('Subscriptions');
            },
            closeWindow() {
                this.closeModal();
            },
        },
        components: {
            Modal
        }
    }
</script>

<style lang="scss" scoped>
    .table {
        thead {
            tr th {
                color: #4b536b
            }
        }

        tbody {
            tr {
                cursor: default;
            }

            td {
                border-top: none
            }

            tr:hover {
                background-color: #fff;
                border: none;
                transform: none;
                cursor: default;
            }
        }
    }

    .center-rule-variables {
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;

        h3 {
            margin-bottom: 1em;
        }

        .table {
            margin: 0 auto;
            width: auto;
        }
    }

    h3 {
        padding: 1rem;
        font-weight: bold;
        font-size: 1.25em;
    }

    .next-icon-space {
        margin-left: 0.5em;
    }

    .footer-btn+.footer-btn {
        margin-left: 1em;
    }

    .modal-content-fixed {
        height: 24.5em;
        box-sizing: border-box;
        display: flex;
        flex-direction: column;
        justify-content: stretch;
    }

    .col-variable {
        width: 25%;
    }

    .col-action {
        width: 15%;
    }

    .col-notes {
        width: 35%;
    }
</style>



// WEBPACK FOOTER //
// PricingRulesLearnMore.vue?6585f4d6